import React from "react";
import { styled } from "@linaria/react";
import { css } from "@linaria/core";
import Legend, {
  defaultRenderSymbol,
  HIDDEN_COLOR,
  Label,
  LegendItem,
} from "@ui/Highcharts/ui/Legend";
import FailureTooltip from "./FailureTooltip";
import Popper from "@ui/Popper";
import { color, fonts } from "@ui";
import LegendTooltip from "./Tooltip";
import classNames from "classnames";
import type { Period } from "@ui/Highcharts/hooks/usePeriod";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { ArrowDownSLine, ArrowRightSLine, FolderSettingsLine } from "@ui/IconV2/react";
import Text from "@ui/Text";
import t from "../../../../../i18n";
import createGlobalState from "@ui/lib/createGlobalState";
import * as io from "io-ts";
import type { Failure, Setup } from "../../../../../api/useSetups";
import type { Project } from "../../../../../api/useProjects";
import type { ManualFailure } from "../../../../../api/useManualFailures";
import { useManualFailures } from "../../../../../api/useManualFailures";
import {
  buildGroupedLegendSeries,
  getFailureDisplayName,
  getFailureGroupOrder,
  isFailureSerie,
  isOtherFailureLegendSerieId,
} from "../failureLegendGrouping";
import { spacings } from "@ui/Theme";
import TooltipV2 from "@ui/TooltipV2";

const NO_FAILURE = "noFailure";

const useFailureLegendGroupOpenState = createGlobalState({
  storage: "session",
  key: "failureLegendGroupOpenState",
  type: io.record(io.string, io.boolean),
  defaultValue: {},
  scoped: true,
});

export interface FailureLegendProps<T extends Highcharts.SeriesOptionsType> {
  chartRefs: React.RefObject<Highcharts.Chart>[];
  project: Project;
  setup: Setup | null;
  series: T[];
  hideColorPicker?: boolean;
  onLegendItemClick: (serieId: string, altClick: boolean) => void;
  renderOtherSeriesTooltip: (serie: T) => React.ReactNode;
  renderSymbol?: (serie: T) => React.ReactNode;
  renderLabel?: (failure: Failure | ManualFailure | undefined, serie: T) => React.ReactNode;
  period?: Period;
  renderFailureActions?: (failure: Failure | ManualFailure) => React.ReactNode;
  getRenderColorItemContent?: (
    failure: Failure | ManualFailure,
    serie: T,
  ) => (color: string) => JSX.Element;
  headerMenu?: React.ReactNode;
  groupByFailureDisplayName?: Record<string, string>;
  onLegendGroupClick?: (serieIds: string[], altClick: boolean) => void;
}

const StyledLegendItem = styled(LegendItem)`
  &.${NO_FAILURE} {
    margin-top: 16px;
  }
`;

const FailurePopper = styled(Popper)`
  border-radius: 0px 4px 4px 4px;
  background-color: ${color.hydrogen};
  color: ${color.carbon800};
  padding: 12px;
  min-width: 100px;
  max-width: 340px;
  box-shadow: 0px 7px 6px #00000029;
  cursor: auto;
  white-space: normal;
`;

const LegendItemContent = styled.div`
  width: 100%;
  display: flex;
  gap: 10px;
  align-items: center;
`;

function FailureLegend<T extends Highcharts.SeriesOptionsType>({
  chartRefs,
  series,
  project,
  period,
  setup,
  hideColorPicker,
  onLegendItemClick,
  renderSymbol = defaultRenderSymbol,
  renderFailureActions,
  renderOtherSeriesTooltip,
  renderLabel = (_failure, serie) => <Label>{getFailureDisplayName(serie)}</Label>,
  getRenderColorItemContent,
  headerMenu,
  groupByFailureDisplayName,
  onLegendGroupClick,
}: FailureLegendProps<T>) {
  const noFailure = series.find(serie => {
    if (!serie.id) throw new Error("serie should always have an id");
    return serie.id.includes("no-failures");
  })?.name;

  const manualFailures = useManualFailures(project.id);

  const ungroupedLabel = t("diagnosis.legend.group_faults.ungrouped");

  const legendSeries = React.useMemo(
    () =>
      buildGroupedLegendSeries({
        series,
        period,
        groupByFailureDisplayName,
        ungroupedLabel,
      }),
    [groupByFailureDisplayName, period, series, ungroupedLabel],
  );

  const groupOrder = React.useMemo(
    () => getFailureGroupOrder(groupByFailureDisplayName, ungroupedLabel),
    [groupByFailureDisplayName, ungroupedLabel],
  );

  const availableGroups = groupOrder;

  const hasHiddenFailureSeries = React.useMemo(
    () => series.some(serie => isFailureSerie(serie) && serie.visible === false),
    [series],
  );
  const hasVisibleFailureSeries = React.useMemo(
    () => series.some(serie => isFailureSerie(serie) && serie.visible !== false),
    [series],
  );

  const shouldRenderGroupHeaders =
    groupByFailureDisplayName !== undefined &&
    !(groupOrder.length === 1 && groupOrder[0] === ungroupedLabel);

  const [groupOpenState, setGroupOpenState] = useFailureLegendGroupOpenState({
    scope: project.id,
  });

  const isGroupCollapsed = React.useCallback(
    (group: string) => groupOpenState[group] === false,
    [groupOpenState],
  );

  React.useEffect(() => {
    if (!shouldRenderGroupHeaders) return;
    setGroupOpenState(prev => {
      const entries = Object.entries(prev).filter(([name]) => availableGroups.includes(name));
      if (entries.length === Object.keys(prev).length) return prev;
      return Object.fromEntries(entries);
    });
  }, [availableGroups, setGroupOpenState, shouldRenderGroupHeaders]);

  const groupBy = React.useCallback(
    (serie: T) => {
      if (!groupByFailureDisplayName || !isFailureSerie(serie)) return null;
      const displayName = getFailureDisplayName(serie);
      if (!displayName) return null;
      return groupByFailureDisplayName[displayName] ?? ungroupedLabel;
    },
    [groupByFailureDisplayName, ungroupedLabel],
  );

  const renderGroupHeader = React.useCallback(
    (group: string, groupSeries: T[]) => {
      if (!groupByFailureDisplayName) return null;
      const isEmptyGroup = groupSeries.length === 0;
      const isGroupFullyHidden =
        groupSeries.length > 0 && groupSeries.every(serie => serie.visible === false);
      const isEmptyGroupHidden = isEmptyGroup && hasHiddenFailureSeries && hasVisibleFailureSeries;
      const isGroupHeaderMuted = isGroupFullyHidden || isEmptyGroupHidden;
      const groupCount = groupSeries.length;
      const groupState = groupOpenState[group];
      const isOpen = groupState === undefined ? !isEmptyGroup : groupState;
      const collapsed = !isOpen;

      const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setGroupOpenState(prev => {
          const currentOpen = prev[group] ?? !isEmptyGroup;
          return { ...prev, [group]: !currentOpen };
        });
      };

      const handleGroupClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (!onLegendGroupClick) return;
        const serieIds = groupSeries.map(serie => serie.id).filter(Boolean) as string[];
        if (serieIds.length === 0) return;
        event.stopPropagation();
        onLegendGroupClick(
          serieIds,
          event.metaKey || event.altKey || event.shiftKey || event.ctrlKey,
        );
      };

      return (
        <div className={failureLegendGroupHeaderBlock}>
          <div className={failureLegendGroupHeaderRow}>
            <button
              type="button"
              className={classNames(failureLegendGroupToggle, {
                [failureLegendGroupToggleHidden]: isGroupFullyHidden,
              })}
              onClick={handleToggle}
            >
              {collapsed ? <ArrowRightSLine size="s" /> : <ArrowDownSLine size="s" />}
            </button>
            <button
              type="button"
              className={failureLegendGroupHeader}
              onClick={handleGroupClick}
              disabled={!onLegendGroupClick || isEmptyGroup}
            >
              <span className={failureLegendGroupHeaderContent}>
                <Text
                  as="span"
                  type="body2SemiBold"
                  className={classNames(failureLegendGroupHeaderLabel, {
                    [failureLegendGroupHeaderLabelHidden]: isGroupHeaderMuted,
                    [failureLegendGroupHeaderLabelEmpty]: isEmptyGroup && !isGroupHeaderMuted,
                  })}
                >
                  {group}
                </Text>
                <Text
                  as="span"
                  type="body2SemiBold"
                  className={classNames(failureLegendGroupHeaderLabel, {
                    [failureLegendGroupHeaderLabelHidden]: isGroupHeaderMuted,
                    [failureLegendGroupHeaderLabelEmpty]: isEmptyGroup && !isGroupHeaderMuted,
                  })}
                >
                  ({groupCount})
                </Text>
              </span>
            </button>
          </div>
          {isEmptyGroup && !collapsed ? (
            <div
              className={classNames(failureLegendGroupEmpty, {
                [failureLegendGroupEmptyHidden]: isGroupHeaderMuted,
              })}
            >
              {t("diagnosis.legend.empty_period")}
            </div>
          ) : null}
        </div>
      );
    },
    [
      groupByFailureDisplayName,
      groupOpenState,
      onLegendGroupClick,
      setGroupOpenState,
      hasHiddenFailureSeries,
      hasVisibleFailureSeries,
    ],
  );

  const renderItem = React.useCallback(
    (serie: T) => {
      const serieName = serie.name;
      if (typeof serieName !== "string") throw new Error("serie.name should be string");

      const failure =
        setup?.failures.find(f => f.name === serie.name) ||
        manualFailures.find(mf => mf.name === serie.name);

      const isOtherSerie = isOtherFailureLegendSerieId(serie.id!, {
        includeNoFailures: true,
      });

      const symbol = renderSymbol(serie);
      const renderColorItemContent =
        getRenderColorItemContent && failure
          ? getRenderColorItemContent(failure, serie)
          : undefined;
      return (
        <StyledLegendItem
          className={classNames(
            { [NO_FAILURE]: serie.name === noFailure },
            {
              [failureLegendGroupedItem]: Boolean(
                shouldRenderGroupHeaders && isFailureSerie(serie),
              ),
            },
          )}
        >
          <FailurePopper
            hover
            keepOpenOnOverlayActive
            overlay={
              isOtherSerie ? (
                <LegendTooltip serieName={serieName} symbol={null}>
                  {renderOtherSeriesTooltip(serie)}
                </LegendTooltip>
              ) : (
                <FailureTooltip
                  failureName={serieName}
                  failure={failure}
                  renderFailureActions={renderFailureActions}
                  project={project}
                  symbol={symbol}
                  renderColorItemContent={renderColorItemContent}
                  hideColorPicker={hideColorPicker}
                />
              )
            }
            groupId="Failures"
            openDelay={1000}
            closeDelay={200}
            openGroupDelay={300}
            gap={0}
            placement="right-start"
          >
            <LegendItemContent>
              {renderSymbol(serie)}
              {renderLabel(failure, serie)}
            </LegendItemContent>
          </FailurePopper>
        </StyledLegendItem>
      );
    },
    [
      noFailure,
      renderSymbol,
      renderLabel,
      renderFailureActions,
      renderOtherSeriesTooltip,
      getRenderColorItemContent,
      setup,
      manualFailures,
      project,
      hideColorPicker,
      shouldRenderGroupHeaders,
    ],
  );

  return (
    <div className={failureLegend}>
      <div className={failureLegendHeader}>
        <Text type="body2SemiBold" className={failureLegendHeaderTitle}>
          {t("commom.widgets.failures.title")}
        </Text>
        {headerMenu ? <div className={failureLegendHeaderMenu}>{headerMenu}</div> : null}
      </div>
      <div className={failureLegendBody}>
        <Legend
          chartRefs={chartRefs}
          series={legendSeries}
          onLegendItemClick={onLegendItemClick}
          period={period}
          renderItem={renderItem}
          groupBy={shouldRenderGroupHeaders ? groupBy : undefined}
          renderGroupHeader={shouldRenderGroupHeaders ? renderGroupHeader : undefined}
          isGroupCollapsed={shouldRenderGroupHeaders ? isGroupCollapsed : undefined}
          groups={shouldRenderGroupHeaders ? groupOrder : undefined}
        />
      </div>
    </div>
  );
}

const failureLegend = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const failureLegendHeader = css`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0 4px 8px;
  gap: 8px;
`;

const failureLegendHeaderTitle = css`
  min-width: 0;
  color: ${color.carbon800};
`;

const failureLegendHeaderMenu = css`
  display: flex;
  justify-content: flex-end;
`;

const failureLegendHeaderMenuButton = css`
  color: ${color.carbon800};
`;

const failureLegendGroupHeaderBlock = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.xxs};
  margin-top: ${spacings.xs};
`;

const failureLegendGroupHeaderRow = css`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 4px;
`;

const failureLegendGroupToggle = css`
  background: none;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  color: ${color.carbon600};
`;

const failureLegendGroupToggleHidden = css`
  color: ${HIDDEN_COLOR};
`;

const failureLegendGroupHeader = css`
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  cursor: pointer;
  flex: 1;
`;

const failureLegendGroupHeaderContent = css`
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
`;

const failureLegendGroupHeaderLabel = css`
  color: ${color.carbon800};
`;

const failureLegendGroupHeaderLabelHidden = css`
  color: ${HIDDEN_COLOR};
`;

const failureLegendGroupHeaderLabelEmpty = css`
  color: ${color.carbon600};
`;

const failureLegendGroupEmpty = css`
  padding: 0 ${spacings.s} ${spacings.xxs} ${spacings.m};
  padding-left: calc(${spacings.m} + 8px);
  color: ${color.carbon600};
  font: ${fonts.body2};
`;

const failureLegendGroupEmptyHidden = css`
  color: ${HIDDEN_COLOR};
`;

const failureLegendGroupedItem = css`
  padding-left: ${spacings.m};
`;

const failureLegendBody = css`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

export interface FailureLegendMenuAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export const FailureLegendMenu = ({
  onClick,
  disabled = false,
}: {
  onClick: () => void;
  disabled?: boolean;
}) => (
  <TooltipV2 overlay={t("diagnosis.legend.group_faults.button")}>
    <ButtonIconV2 className={failureLegendHeaderMenuButton} disabled={disabled} onClick={onClick}>
      <FolderSettingsLine size="m" />
    </ButtonIconV2>
  </TooltipV2>
);

export const useLegendGroupClick = <T extends Highcharts.SeriesOptionsType>(
  visibleSeries: T[],
  setSerieVisibility: (serieId: string, visible: boolean) => void,
) =>
  React.useCallback(
    (groupSerieIds: string[], altClick: boolean) => {
      if (groupSerieIds.length === 0) return;
      if (altClick) {
        const allVisible = groupSerieIds.every(
          serieId => visibleSeries.find(serie => serie.id === serieId)?.visible !== false,
        );
        for (const serieId of groupSerieIds) {
          setSerieVisibility(serieId, !allVisible);
        }
        return;
      }

      const isGroupIsolated = visibleSeries.every(serie => {
        const serieId = serie.id;
        if (!serieId) return true;
        const isInGroup = groupSerieIds.includes(serieId);
        const isVisible = serie.visible !== false;
        return isInGroup ? isVisible : !isVisible;
      });

      for (const serie of visibleSeries) {
        const serieId = serie.id;
        if (!serieId) continue;
        setSerieVisibility(serieId, isGroupIsolated ? true : groupSerieIds.includes(serieId));
      }
    },
    [setSerieVisibility, visibleSeries],
  );

export default React.memo(FailureLegend) as typeof FailureLegend;
